/**
 * MacroBar — accessible horizontal stacked bar for macronutrient proportions.
 *
 * Accessibility:
 *   - Each segment has role="img" + aria-label with grams value (not color-only).
 *   - Text labels adjacent to bar so screen readers and color-blind users get the info.
 *   - No dangerouslySetInnerHTML; all values via React text bindings.
 *
 * Design:
 *   - Protein: dark (zinc-600 / blue-toned)
 *   - Carbs: gray (zinc-500)
 *   - Fats: light (zinc-400)
 *   - Proportional widths derived from pct fields.
 */

import type { MacroSplit } from "@autodieta/shared";
import { formatMacro } from "../utils/format.js";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface MacroBarProps {
  macros: MacroSplit;
}

// ---------------------------------------------------------------------------
// MacroBar
// ---------------------------------------------------------------------------

export function MacroBar({ macros }: MacroBarProps): JSX.Element {
  const { proteinPct, carbPct, fatPct, proteinG, carbG, fatG } = macros;

  // Display-only normalization: scale the three segment widths so they always
  // sum to 100% and the bar never overflows on extreme days (e.g. low-energy,
  // high g/kg where proteinPct + fatPct can exceed 100). This does NOT touch the
  // engine — aria-labels below still show the true grams/% values.
  const display = normalizeWidths(proteinPct, carbPct, fatPct);

  return (
    <div className="flex flex-col gap-2">
      {/* Stacked bar */}
      <div
        className="flex h-3 w-full overflow-hidden rounded-full"
        aria-label="Distribución de macronutrientes"
      >
        <div
          role="img"
          aria-label={`Proteína: ${formatMacro(proteinG)} g (${formatMacro(proteinPct)}%)`}
          data-segment="protein"
          style={{ width: `${display.protein}%` }}
          className="bg-blue-500 transition-all"
        />
        <div
          role="img"
          aria-label={`Carbohidratos: ${formatMacro(carbG)} g (${formatMacro(carbPct)}%)`}
          data-segment="carbs"
          style={{ width: `${display.carb}%` }}
          className="bg-zinc-500 transition-all"
        />
        <div
          role="img"
          aria-label={`Grasas: ${formatMacro(fatG)} g (${formatMacro(fatPct)}%)`}
          data-segment="fats"
          style={{ width: `${display.fat}%` }}
          className="bg-zinc-300 transition-all"
        />
      </div>

      {/* Text labels — not color-only */}
      <div className="grid grid-cols-3 gap-1 text-xs">
        <MacroLabel
          label="Proteína"
          grams={proteinG}
          pct={proteinPct}
          colorClass="text-blue-400"
          dotClass="bg-blue-500"
        />
        <MacroLabel
          label="Carbos"
          grams={carbG}
          pct={carbPct}
          colorClass="text-zinc-300"
          dotClass="bg-zinc-500"
        />
        <MacroLabel
          label="Grasas"
          grams={fatG}
          pct={fatPct}
          colorClass="text-zinc-400"
          dotClass="bg-zinc-300"
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Display-only width normalization
// ---------------------------------------------------------------------------

interface SegmentWidths {
  protein: number;
  carb: number;
  fat: number;
}

/**
 * Scale the three macro percentages proportionally so their widths sum to 100%
 * for DISPLAY purposes only. Prevents the stacked bar from overflowing when the
 * raw percentages sum to more (or less) than 100 on extreme days.
 *
 * Pure presentation helper — never feeds back into the engine; aria-labels keep
 * the true grams/% values. When the total is non-positive (degenerate input),
 * returns zero widths so nothing renders rather than dividing by zero.
 */
function normalizeWidths(proteinPct: number, carbPct: number, fatPct: number): SegmentWidths {
  const total = proteinPct + carbPct + fatPct;
  if (total <= 0) {
    return { protein: 0, carb: 0, fat: 0 };
  }
  return {
    protein: (proteinPct / total) * 100,
    carb: (carbPct / total) * 100,
    fat: (fatPct / total) * 100,
  };
}

// ---------------------------------------------------------------------------
// Internal label component
// ---------------------------------------------------------------------------

interface MacroLabelProps {
  label: string;
  grams: number;
  pct: number;
  colorClass: string;
  dotClass: string;
}

function MacroLabel({ label, grams, pct, colorClass, dotClass }: MacroLabelProps): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <div className="flex items-center gap-1">
        <span className={`h-2 w-2 shrink-0 rounded-full ${dotClass}`} aria-hidden="true" />
        <span className={`text-xs font-medium ${colorClass}`}>{label}</span>
      </div>
      <span className="text-zinc-300 font-semibold">{formatMacro(grams)} g</span>
      <span className="text-zinc-500">{formatMacro(pct)}%</span>
    </div>
  );
}
